import {
    HttpClient,
    HttpParams,
    HttpResponse,
    HttpStatusCode
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    UserAccessCollection,
    UserAccessCollectionResponse,
    UserModuleAccessCollection
} from '@predictiveindex/client-permissions-lib';
import { map, Observable } from 'rxjs';
import { PI } from '../../constants-types/PI.model';
import {
    BulkShareUserRequest,
    SharedObjectType,
    ShareUserResponse,
    ShareUserUpdateRequest,
    SharingUser,
    SharingUserServer
} from '../../core-data-model';
import {
    ApiService,
    InitDataService,
    UserProfileSettingsService
} from '../../system-services';
import { SharingUserTransformer } from './sharing-user.transform';

export interface ISharingUserApi {
    getEntityUsers(
        entityId: string,
        entityType: string
    ): Promise<SharingUser[]>;
    updateUsersAccessPolicy(shareUpdate: ShareUserUpdateRequest): Promise<any>;
    revokeUsersAccessPolicy(revokeUpdate: ShareUserUpdateRequest): Promise<any>;
    shareWithUsers(
        shareUser: BulkShareUserRequest
    ): Promise<[ShareUserResponse, HttpResponse<ShareUserResponse>]>;
}

@Injectable({ providedIn: 'root' })
export class SharingUserApiDefault implements ISharingUserApi {
    userApiUrl: string;
    private readonly companyGuid: string = null;

    constructor(
        private api: ApiService,
        private http: HttpClient,
        private userProfile: UserProfileSettingsService,
        private sharingUserTransformer: SharingUserTransformer,
        private initData: InitDataService
    ) {
        this.userApiUrl = PI?.appSettings?.userApiUrl;
        this.companyGuid = this.initData.profile.settings.currentCompany;
    }

    fetchOrganizationUsers(
        orgAdminFilter?: boolean,
        includeDeleted?: boolean
    ): Observable<UserModuleAccessCollection[]> {
        let params;
        if (orgAdminFilter) {
            if (includeDeleted) {
                params = {
                    orgAdminFilter: orgAdminFilter,
                    includeDeleted: includeDeleted
                };
            } else {
                params = { orgAdminFilter: orgAdminFilter };
            }
        } else if (includeDeleted) {
            params = { includeDeleted: includeDeleted };
        }

        return this.http.get<UserModuleAccessCollection[]>(
            this.getApiEndpoint('/users/collection'),
            { params }
        );
    }

    fetchOrganizationAdminUsers(): Observable<UserModuleAccessCollection[]> {
        return this.http.get<UserModuleAccessCollection[]>(
            this.getApiEndpoint('/users/admincollection')
        );
    }

    fetchModuleSpecificOrganizationUsers(
        entityType: SharedObjectType,
        searchText?: string,
        filterOrgAdmins?: boolean,
        excludeModuleAdmins?: boolean
    ): Observable<UserAccessCollection[]> {
        const params = new HttpParams();

        if (searchText) {
            params.set('searchText', searchText);
        }
        if (filterOrgAdmins) {
            params.set('filterOrgAdmins', filterOrgAdmins);
        }
        if (excludeModuleAdmins) {
            params.set('excludeModuleAdmins', excludeModuleAdmins);
        }

        return this.http
            .get<UserAccessCollectionResponse>(
                this.getApiEndpoint('/users/collections', true, entityType),
                { params }
            )
            .pipe(
                map(
                    (x: UserAccessCollectionResponse) => x.userAccessCollections
                )
            );
    }

    async updateUsersAccessPolicy(
        shareUpdate: ShareUserUpdateRequest
    ): Promise<any> {
        const url = `/api/v1/companies/${this.userProfile.currentCompany}/users/batchAccessPolicyChange?`;
        return this.api
            .put<ShareUserUpdateRequest>(url, shareUpdate, this.userApiUrl)
            .then(([responseBody, httpResponse]) => {
                if (httpResponse.status !== HttpStatusCode.Ok) {
                    return Promise.reject();
                }
                return responseBody;
            });
    }

    async revokeUsersAccessPolicy(
        revokeUpdate: ShareUserUpdateRequest
    ): Promise<any> {
        return this.updateUsersAccessPolicy(revokeUpdate);
    }

    async getEntityUsers(
        entityId: string,
        entityType: string
    ): Promise<SharingUser[]> {
        const url = `/api/v1/companies/${this.userProfile.currentCompany}/users/accessPolicy?entityId=${entityId}&entityType=${entityType}`;
        return this.api
            .get<SharingUserServer[]>(url, null, null, this.userApiUrl)
            .then(([response, httpResponse]) => {
                if (httpResponse.status !== HttpStatusCode.Ok) {
                    return Promise.reject();
                }
                return this.sharingUserTransformer.transformServerToClient(
                    response
                );
            });
    }

    async shareWithUsers(
        shareUser: BulkShareUserRequest
    ): Promise<[ShareUserResponse, HttpResponse<ShareUserResponse>]> {
        const url = `/api/v1/companies/${this.userProfile.currentCompany}/users/entity/sharewithuser/bulk`;
        return this.api.post<BulkShareUserRequest, ShareUserResponse>(
            url,
            shareUser,
            this.userApiUrl
        );
    }

    private getApiEndpoint(
        endpoint: string,
        withCompanyId = true,
        entityType?: SharedObjectType
    ): string {
        let baseApi = PI?.appSettings?.userApiUrl ?? '';
        let apiVersion = 'v1';

        if (entityType && entityType === SharedObjectType.Job) {
            baseApi = PI?.appSettings?.hireApiUrl;
            apiVersion = 'v5-hire';
        } else if (entityType && entityType === SharedObjectType.Team) {
            baseApi = PI?.appSettings?.designApiUrl;
            apiVersion = 'v5';
        }

        baseApi = baseApi.replace(/\/$/, '').concat('/');
        endpoint = endpoint.replace(/(^\/{0,2})|(\/{0,2}$)/g, '');

        return withCompanyId
            ? baseApi.concat(
                  `api/${apiVersion}/companies/${this.companyGuid}/${endpoint}`
              )
            : baseApi.concat(endpoint);
    }
}
